import {
  OptionGroupDef,
  OptionStore,
  RowTextValue,
  emptyChooseOneValue,
  emptyGroupValue,
  isGroupValue,
} from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/readmission-f2f";
import {
  NOTE_ANSWER_KEY_OVERRIDES,
  NOTE_FREE_TEXT_ANSWER_KEY,
  NOTE_ROW_ANSWER_KEYS,
  NOTE_TEXT_SEPARATOR,
} from "./initial-f2f-constant";
import { NoteAnswer, NoteLine, NoteSection } from "./initial-f2f-types";
import { stripTrailingDot } from "./initial-f2f-note-lines";

export type Answers = Record<string, NoteAnswer>;

const toSnakeCase = (value: string): string =>
  value
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();

export const getAnswerKey = (key: string): string =>
  NOTE_ANSWER_KEY_OVERRIDES[key] ?? toSnakeCase(key);

const getRowAnswerKey = (label: string): string =>
  NOTE_ROW_ANSWER_KEYS[label] ?? toSnakeCase(label);

/** `textLines` keep the legacy sentence text; `lines` default to one untitled line per text line. */
export const toSection = (
  answers: Answers,
  textLines: string[],
  lines: NoteLine[] = textLines.map((text) => ({
    text: stripTrailingDot(text),
  }))
): NoteSection | undefined => {
  const hasAnswers = Object.keys(answers).length > 0;
  return hasAnswers || textLines.length
    ? {
        ...(hasAnswers && { answers }),
        text: textLines.join(NOTE_TEXT_SEPARATOR),
        ...(lines.length && { lines }),
      }
    : undefined;
};

export const buildGroupAnswers = (
  groups: OptionGroupDef[],
  store: OptionStore
): Answers =>
  groups.reduce<Answers>((answers, group) => {
    const current = store[group.key];
    if (!current) return answers;
    const freeText = current.freeText.trim();
    if (isGroupValue(current)) {
      if (!current.selected.length && !freeText) return answers;
      answers[getAnswerKey(group.key)] = {
        value: current.selected.join(", "),
        selected: current.selected,
        ...(Object.keys(current.details).length && {
          details: current.details,
        }),
        ...(freeText && { freeText }),
      };
    } else if (current.selected || freeText) {
      const detail = current.detail?.trim();
      answers[getAnswerKey(group.key)] = {
        value: current.selected,
        ...(detail &&
          current.selected && { details: { [current.selected]: detail } }),
        ...(freeText && { freeText }),
      };
    }
    return answers;
  }, {});

export const parseGroupAnswers = (
  groups: OptionGroupDef[],
  answers: Answers
): OptionStore =>
  groups.reduce<OptionStore>((store, group) => {
    const answer = answers[getAnswerKey(group.key)];
    if (!answer) return store;
    const freeText = answer.freeText ?? "";
    const value = typeof answer.value === "string" ? answer.value : "";
    store[group.key] = group.chooseOne
      ? {
          ...emptyChooseOneValue(),
          selected: value,
          freeText,
          detail: answer.details?.[value] ?? "",
        }
      : {
          ...emptyGroupValue(),
          selected: answer.selected ?? (value ? [value] : []),
          details: answer.details ?? {},
          freeText,
        };
    return store;
  }, {});

export const buildRowAnswers = (
  labels: string[],
  value: RowTextValue,
  freeText: string
): Answers => {
  const answers = labels.reduce<Answers>((rows, label) => {
    const entry = value[label];
    if (entry?.checked) {
      rows[getRowAnswerKey(label)] = { value: entry.text.trim() || true };
    }
    return rows;
  }, {});
  if (freeText.trim()) {
    answers[NOTE_FREE_TEXT_ANSWER_KEY] = { value: freeText.trim() };
  }
  return answers;
};

export const parseRowAnswers = (
  labels: string[],
  answers: Answers
): RowTextValue =>
  labels.reduce<RowTextValue>((value, label) => {
    const answer = answers[getRowAnswerKey(label)];
    if (answer) {
      value[label] = {
        checked: true,
        text: typeof answer.value === "string" ? answer.value : "",
      };
    }
    return value;
  }, {});

export const parseFreeText = (answers: Answers): string => {
  const value = answers[NOTE_FREE_TEXT_ANSWER_KEY]?.value;
  return typeof value === "string" ? value : "";
};
